import {
  ApiApplicationCredentials,
  ApplicationCredentialsModel,
} from '../../models/api/applicationCredentialsModel';
import {CompanyModel} from '../../models/api/companyModel';
import {LocalApplicationModel} from '../../models/api/localApplicationModel';
import {
  ApiServer,
  serializeServer,
  ServerAuthenticationStrategiesEnum,
  ServerModel,
  UnsavedServerModel,
  WritableServerModel,
} from '../../models/api/serverModel';
import {deleteAsync, postApiAsync, putApiAsync} from '../../services/http/http';
import {ListMutation} from '../helpers/listMutation';
import {ResourceTableCommonPageType} from '../helpers/resourceListPagination/resourceListPaginationHelpers';

/*
 * Types.
 */

export type ServerListPageType = ResourceTableCommonPageType<ServerModel>;

export enum ServerMutationsEnum {
  CREATE = 'create',
  EDIT = 'edit',
  DELETE = 'delete',
  SAVE_CREDENTIALS = 'save_credentials',
}

interface CreateMutationAction {
  type: ServerMutationsEnum.CREATE;
  payload: {
    application: LocalApplicationModel;
    server: UnsavedServerModel;
    credentials: ApplicationCredentialsModel;
  };
}

interface EditMutationAction {
  type: ServerMutationsEnum.EDIT;
  payload: {
    id: WritableServerModel['id'];
    url: WritableServerModel['url'];
    server: UnsavedServerModel;
    credentials: ApplicationCredentialsModel;
  };
}

interface DeleteMutationAction {
  type: ServerMutationsEnum.DELETE;
  payload: {
    company: CompanyModel;
    serverIds: ReadonlyArray<ServerModel['id']>;
  };
}

interface SaveCredentialsAction {
  type: ServerMutationsEnum.SAVE_CREDENTIALS;
  payload: {
    server: ServerModel;
    credentials: ApplicationCredentialsModel;
  };
}

export type ServerListMutation =
  | CreateMutationAction
  | EditMutationAction
  | DeleteMutationAction
  | SaveCredentialsAction;

/*
 * Mutations.
 */

const createServerMutation: ListMutation<ServerListPageType, CreateMutationAction, void> = {
  mutationKey: ServerMutationsEnum.CREATE,

  async runSideEffect(payload) {
    const {application, server, credentials} = payload;

    const serializedPayload = serializeServer(server);
    const createdServer = await postApiAsync(
      ApiServer,
      `${application.url}/application_servers`,
      serializedPayload,
    );

    saveCredentials(createdServer, credentials);
  },

  runOptimisticUpdate(data, payload, pageIndex) {
    // Only update the first page.
    if (pageIndex !== 0) {
      return data;
    }

    // Use dummy `id`, `url` and `createdAt` while waiting for the server to respond.
    const newServer: ServerModel = {
      isWritable: true,
      id: 0,
      url: '',
      installationUrl: '',
      createdAt: Date.now(),
      ...payload.server,
    };

    return {
      ...data,
      items: [newServer, ...data.items],
    };
  },
};

const editServerMutation: ListMutation<ServerListPageType, EditMutationAction, void> = {
  mutationKey: ServerMutationsEnum.EDIT,

  async runSideEffect(payload) {
    const {url, server, credentials} = payload;

    const serializedPayload = serializeServer(server);
    const updatedServer = await putApiAsync(ApiServer, url, serializedPayload);

    saveCredentials(updatedServer, credentials);
  },

  runOptimisticUpdate(data, payload) {
    return {
      ...data,
      items: data.items.map((server) => {
        // TS needs to know that we're optimistically updating a WritableServerModel
        if (payload.id === server.id && server.isWritable) {
          return {
            ...server,
            ...payload.server,
          };
        }
        return server;
      }),
    };
  },
};

const deleteServerMutation: ListMutation<ServerListPageType, DeleteMutationAction, void> = {
  mutationKey: ServerMutationsEnum.DELETE,

  async runSideEffect(payload) {
    const {company, serverIds} = payload;

    await deleteAsync(`${company.url}/application_servers_batch`, {
      application_server_ids: serverIds,
    });
  },

  runOptimisticUpdate(data, payload) {
    return {
      ...data,
      items: data.items.filter((server) => !payload.serverIds.includes(server.id)),
    };
  },
};

const saveCredentialsMutation: ListMutation<ServerListPageType, SaveCredentialsAction, void> = {
  mutationKey: ServerMutationsEnum.SAVE_CREDENTIALS,

  async runSideEffect(payload) {
    const {server, credentials} = payload;

    await saveCredentials(server, credentials);
  },

  runOptimisticUpdate(data, payload) {
    return data;
  },
};

async function saveCredentials(server: ServerModel, credentials: ApplicationCredentialsModel) {
  if (credentials.type !== ServerAuthenticationStrategiesEnum.NONE) {
    await putApiAsync(ApiApplicationCredentials, `${server.installationUrl}/credentials`, {
      credentials,
    });
  }
}

export const serverListMutations = [
  createServerMutation,
  editServerMutation,
  deleteServerMutation,
  saveCredentialsMutation,
] as const;
